import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, filter, lastValueFrom } from 'rxjs';
import {
	Lifeskill,
	LifeskillPagedResult
} from 'src/app/api/life-skills-api/models';
import { LifeSkillService } from 'src/app/api/life-skills-api/services';
@Injectable({
	providedIn: 'root'
})
export class LifeskillsHelperService {
	private lifeSkillService = inject(LifeSkillService);

	private lifeskills: BehaviorSubject<LifeskillPagedResult> =
		new BehaviorSubject<LifeskillPagedResult>(null);

	public lifeskills$: Observable<LifeskillPagedResult> = this.lifeskills.pipe(
		filter((lifeskills) => lifeskills != null && lifeskills != undefined)
	);

	public initialize(): void {
		this.lifeSkillService.getLifeskill$Json().subscribe({
			next: (response) => {
				if (
					response &&
					response.results &&
					Array.isArray(response.results)
				) {
					this.lifeskills.next(response);
				} else {
					console.error(
						'Unexpected API response structure:',
						response
					);
				}
			},
			error: (error: unknown) => {
				console.error('Error fetching lifeskills:', error);
			},
			complete: () => {
				// Lifeskills initialization complete
			}
		});
	}

	public getPagedLifeskills(page: number, pageSize: number): void {
		this.lifeSkillService
			.getLifeskill$Json({
				page: page,
				pageSize: pageSize
			})
			.subscribe({
				next: (response) => {
					if (
						response &&
						response.results &&
						Array.isArray(response.results)
					) {
						this.lifeskills.next(response);
					} else {
						console.error(
							'Unexpected API response structure:',
							response
						);
					}
				},
				error: (error: unknown) => {
					console.error('Error fetching lifeskills:', error);
				},
				complete: () => {
					// Lifeskills initialization complete
				}
			});
	}
	public getPagedLifeskills$(
		page: number,
		pageSize: number
	): Observable<LifeskillPagedResult> {
		return this.lifeSkillService.getLifeskill$Json({
			page: page,
			pageSize: pageSize
		});
	}

	public getLifeskillById(id: string): Observable<Lifeskill> {
		return this.lifeSkillService.getLifeskillId$Json({ id });
	}

	public async addLifeskill(lifeskill: Lifeskill): Promise<void> {
		// Ensure slug is the lowercase version of the name
		lifeskill.slug = lifeskill.name.toLowerCase().replace(/\s+/g, '-');

		// Ensure reactions is an array
		if (!Array.isArray(lifeskill.reactions)) {
			lifeskill.reactions = [];
		}

		return await lastValueFrom(
			this.lifeSkillService.postLifeskill$Json({ body: lifeskill })
		)
			.then((_response: Lifeskill) => {
				this.refreshLifeskills();
			})
			.catch((error: unknown) => {
				console.error('Add Lifeskill Error:', error);
				return Promise.resolve();
			});
	}

	public async editLifeskill(lifeskill: Lifeskill): Promise<void> {
		lifeskill.slug = lifeskill.name.toLowerCase().replace(/\s+/g, '-');

		return await lastValueFrom(
			this.lifeSkillService.putLifeskillId({
				id: lifeskill.id,
				body: lifeskill
			})
		)
			.then(() => {
				this.refreshLifeskills();
			})
			.catch((error: unknown) => {
				console.error('Edit Lifeskill Error:', error);
				return Promise.resolve();
			});
	}

	public removeLifeskill(_id: string): void {
		/*
		 * HELLOTOMO:
		 * Replace to call GET ALL API if lifeskill is removed (unless paging is used)
		 */
	}

	public getAllLifeskills(): LifeskillPagedResult {
		return this.lifeskills.value;
	}

	private refreshLifeskills(): void {
		this.lifeSkillService.getLifeskill$Json().subscribe({
			next: (response: LifeskillPagedResult) => {
				if (
					response &&
					response.results &&
					Array.isArray(response.results)
				) {
					this.lifeskills.next(response);
				} else {
					console.error(
						'Unexpected API response structure during refresh:',
						response
					);
				}
			},
			error: (error: unknown) => {
				console.error('Error refreshing lifeskills:', error);
			},
			complete: () => {
				// Lifeskills refresh complete
			}
		});
	}
}
